import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LockDto } from 'services/locks/locks.service';
import { useAPILocks } from 'services/locks/api-locks.service';
import { useAPIKeys } from 'services/keys/api-keys.service';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import {
    ALL_DAYS,
    buildKeyPayload,
    buildKeyValidityLabel,
    isDraftComplete,
    KeyDraft,
    toggleDay,
    VigenciaPreset,
} from './key-schedule';
import { KeyAccessController, KeySubmitOutcome, KeyValidationError } from './interfaces';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// navigateOnNetworkError: false — el fallo de carga y el del submit tienen su
// propio estado inline (error con reintento, banner de red); navegar a
// /mobile/offline desmontaría la pantalla y perdería email, cerradura y
// vigencia ya elegidos.
export const useKeyAccessController = (
    lockService = useAPILocks({ navigateOnNetworkError: false }),
    keyService = useAPIKeys({ navigateOnNetworkError: false }),
): KeyAccessController => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { translate } = useTranslator();
    const accessToken = searchParams.get('access_token') || '';
    const queryLockId = (searchParams.get('lock_id') || '').toLowerCase();

    const [locks, setLocks] = useState<LockDto[]>([]);
    const [locksLoaded, setLocksLoaded] = useState(false);
    const [locksLoadError, setLocksLoadError] = useState(false);
    const [selectedLock, setSelectedLock] = useState<LockDto | undefined>();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [assigneeEmail, setAssigneeEmail] = useState('');
    const [panelOpen, setPanelOpen] = useState(false);
    const [restrictionsOpen, setRestrictionsOpen] = useState(false);
    const [preset, setPreset] = useState<VigenciaPreset>('always');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dow, setDow] = useState(ALL_DAYS);
    const [startTime, setStartTime] = useState('00:00');
    const [endTime, setEndTime] = useState('23:59');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasNetworkError, setHasNetworkError] = useState(false);
    const [outcome, setOutcome] = useState<KeySubmitOutcome | undefined>();

    // Reinvocable: es el punto de reintento cuando la carga falla por red. Sin
    // esto un hipo de conexión deja la pantalla inservible para siempre.
    //
    // locksLoadError se limpia recién en el .then, no antes de lanzar el
    // pedido: si se limpiara antes, el retry quedaría "sin error" un instante
    // antes de que la lista realmente llegue, y un waitFor sobre locksLoadError
    // podría resolver de casualidad antes de que locks se actualice.
    const loadLocks = () => {
        lockService
            .listLocksInKeyCreation(accessToken)
            .then((list: LockDto[]) => {
                setLocks(list);
                setLocksLoaded(true);
                setLocksLoadError(false);
                const fromQuery = queryLockId ? list.find((l) => l.id === queryLockId) : undefined;
                if (fromQuery) {
                    setSelectedLock(fromQuery);
                } else if (list.length === 1) {
                    setSelectedLock(list[0]);
                }
            })
            .catch(() => {
                setLocksLoadError(true);
                setLocksLoaded(true);
            });
    };

    useEffect(() => {
        if (!accessToken) {
            navigate('/mobile/404');
            return;
        }
        loadLocks();
    }, []);

    const draft: KeyDraft | undefined = selectedLock
        ? { lock: selectedLock, assigneeEmail, preset, startDate, endDate, dow, startTime, endTime }
        : undefined;

    const validationError: KeyValidationError | undefined = (() => {
        if (!selectedLock || !draft) {
            return 'lock';
        }
        if (!assigneeEmail.trim()) {
            return 'email';
        }
        if (!EMAIL_RE.test(assigneeEmail.trim())) {
            return 'email-format';
        }
        if (!isDraftComplete(draft)) {
            return 'incomplete';
        }
        return undefined;
    })();

    const canSubmit = !validationError && !isSubmitting;

    // Sin useMemo a propósito: la etiqueta de un preset acotado depende del reloj,
    // y memoizarla la congelaría en el resultado del primer render.
    const validityLabel = draft ? buildKeyValidityLabel(draft, translate) : '';

    // El prefijo alcanza para distinguir los dos conflictos. El email NO se saca
    // del mensaje: ya lo tenemos en el formulario, y partir el string por
    // espacios (como hacía el código viejo) se rompe con cualquier texto extra.
    const outcomeForError = (err: unknown): KeySubmitOutcome | undefined => {
        const res = (err as { response?: { data?: { code?: number; msg?: string } } })?.response;
        if (res?.data?.code !== 409) {
            return undefined;
        }
        const msg = (res.data.msg || '').trim();
        if (msg.startsWith('denied')) {
            return { kind: 'denied' };
        }
        if (msg.startsWith('user')) {
            return { kind: 'no-account' };
        }
        return undefined;
    };

    const submit = () => {
        if (!canSubmit || !draft) {
            return;
        }
        setIsSubmitting(true);
        setHasNetworkError(false);
        setOutcome(undefined);

        return keyService
            .createKey(accessToken, buildKeyPayload(draft))
            .then((r) => {
                // 202 = ya tiene una llave en esa cerradura. El id que vuelve es
                // el registro que hay que pasarle a overwriteKeys.
                if (r.status === 202) {
                    setOutcome({ kind: 'already-has-key', recordId: r.data.id });
                    return;
                }
                setOutcome({ kind: 'created', summary: r.data });
            })
            .catch((err: unknown) => {
                const conflictOutcome = outcomeForError(err);
                if (conflictOutcome) {
                    setOutcome(conflictOutcome);
                    return;
                }
                setHasNetworkError(true);
            })
            .finally(() => setIsSubmitting(false));
    };

    const confirmOverwrite = () => {
        if (!outcome || outcome.kind !== 'already-has-key') {
            return;
        }
        const recordId = outcome.recordId;
        setIsSubmitting(true);
        setHasNetworkError(false);

        return keyService
            .overwriteKeys(accessToken, { id: recordId })
            .then((r) => setOutcome({ kind: 'created', summary: r.data }))
            .catch(() => setHasNetworkError(true))
            .finally(() => setIsSubmitting(false));
    };

    return {
        locks,
        locksLoaded,
        locksLoadError,
        selectedLock,
        pickerOpen,
        assigneeEmail,
        panelOpen,
        restrictionsOpen,
        preset,
        startDate,
        endDate,
        dow,
        startTime,
        endTime,
        isSubmitting,
        hasNetworkError,
        outcome,
        canSubmit,
        validationError,
        validityLabel,
        setAssigneeEmail,
        openPicker: () => setPickerOpen(true),
        closePicker: () => setPickerOpen(false),
        // No resetea nada más: la regla 2 pide conservar todo lo que sirva.
        onLockPicked: (l: LockDto) => {
            setSelectedLock(l);
            setPickerOpen(false);
        },
        togglePanel: () => setPanelOpen((v) => !v),
        toggleRestrictions: () => setRestrictionsOpen((v) => !v),
        setPreset,
        setStartDate,
        setEndDate,
        setDow,
        onToggleDay: (bit: number) => setDow((d) => toggleDay(d, bit)),
        setStartTime,
        setEndTime,
        submit,
        confirmOverwrite,
        cancelOverwrite: () => setOutcome(undefined),
        dismissOutcome: () => setOutcome(undefined),
        retryLoadLocks: loadLocks,
        onClose: () => navigate(-1),
    };
};
